import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Types
import { AppDispatch } from 'types/RootState.types';
import { SessionManagerProps } from './SessionManager.types';

// Config
import sessionManagersState from './SessionManager.state';
import { APPS_AUTHENTICATED_HANDOVER, APPS_GUEST_HANDOVER } from '@constants/actionConstants';
import { showDisableSiteSpinner } from '@redux/actions/app/appActions';
import { handleAppsHandover, handleWebHandover } from '@redux/actions/bff/bffActions';
import { getHandoverParams } from '@utils/app/getHandoverParams';

const SessionManager = ({ children, location }: SessionManagerProps) => {
  const dispatch: AppDispatch = useDispatch();

  const {
    basketId,
    biom = '',
    email,
    envId,
    express,
    fromAuth0,
    jsessionId,
    monetateId,
    vid = '',
  } = getHandoverParams(location?.search);

  const {
    sessionHandoverComplete,
    sessionHandoverFailed,
    getLoginPageComplete,
    isAppsGuestHandover,
    isAppsAuthenticatedHandover,
    auth0AccessToken,
    requestAccessToken,
  } = useSelector(sessionManagersState);

  useEffect(() => {
    if (isAppsAuthenticatedHandover) {
      dispatch(showDisableSiteSpinner());
      return;
    }

    if (isAppsGuestHandover) {
      dispatch(showDisableSiteSpinner());
      dispatch(
        handleAppsHandover({
          actionType: APPS_GUEST_HANDOVER,
          basketId,
          biom,
          email,
          environmentId: envId,
          express,
          fromAuth0,
          jsessionId,
          monetateId,
          visitorId: vid,
        }),
      );
      return;
    }

    if (!sessionHandoverComplete && !sessionHandoverFailed) {
      dispatch(
        handleWebHandover({
          express,
          basketId,
        }),
      );
    }
  }, []);

  useEffect(() => {
    if (isAppsAuthenticatedHandover) {
      if (typeof requestAccessToken === 'function' && !auth0AccessToken) {
        requestAccessToken();
        return;
      }

      if (auth0AccessToken) {
        window.timeToAuth0Complete = Date.now();
        dispatch(
          handleAppsHandover({
            accessToken: auth0AccessToken,
            actionType: APPS_AUTHENTICATED_HANDOVER,
            basketId,
            biom,
            environmentId: envId,
            express,
            fromAuth0,
            jsessionId,
            monetateId,
            visitorId: vid,
          }),
        );
      }
    }
  }, [auth0AccessToken, requestAccessToken, isAppsAuthenticatedHandover]);

  return getLoginPageComplete ? children : null;
};

export default SessionManager;
